import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { breadcrumbSchema, datasetSchema } from "@/lib/schema";
import { PAL_COUNT, PAL_SCHEMA_SAMPLE } from "@/lib/seo-snapshots";

const PalsIndexContent = lazy(() => import("@/components/pages/PalsIndexContent").then((m) => ({ default: m.PalsIndexContent })));

const FAQ = [
  {
    q: "How many Pals are in the Pal Stars database?",
    a: `The database currently holds ${PAL_COUNT} Pal entries, each with a full stat sheet covering HP, attack, defense, ride speed, work suitabilities, partner skill, drops, spawn region and matching Palworld TCG card data.`,
  },
  {
    q: "How do I find a Pal by element or rarity in Palworld?",
    a: "Use the element and rarity dropdowns above the grid. The filters combine with the search box, so you can search a partner skill or spawn region and narrow it to a single element and rarity at the same time. Your filters stay in the page URL, so the filtered list can be bookmarked and shared.",
  },
  {
    q: "What does each Pal page show?",
    a: "Clicking any card opens that Pal's own page with combat stats, work speed, mount and capture data, move list, skill tree, drops, breeding notes and its printed Palworld Official Card Game card number.",
  },
  {
    q: "Where can I see which Pals are the strongest?",
    a: "The tier list ranks every Pal SS to C for PvE combat, PvP, base work and mount speed, with the same element and rarity filters used here.",
  },
];

function faqSchema(items: typeof FAQ, url: string) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      url,
      mainEntity: items.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }),
  };
}


function PalsPending() {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld Pal Database</h1>
      <p className="aeo-answer mt-2 max-w-3xl text-muted-foreground">Search and filter every Pal in Palworld by element, rarity, stats, card data and partner skill.</p>
      <section className="glass rounded-2xl p-6 mt-10">
        <h2 className="text-xl font-bold">Palworld Pal database FAQ</h2>
        <div className="mt-4 divide-y divide-border/50">
          {FAQ.map((f, i) => (
            <details key={f.q} className="py-3" open={i === 0}>
              <summary className="cursor-pointer font-semibold">{f.q}</summary>
              <p className="aeo-answer mt-2 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}

function RouteComponent() {
  return <Suspense fallback={<PalsPending />}><PalsIndexContent /></Suspense>;
}

export const Route = createFileRoute("/pals/")({
  pendingComponent: PalsPending,
  component: RouteComponent,
  head: () => ({
    meta: [
      // Exact-match head term "palworld pals" leads the title; "pal world pals"
      // is covered in the on-page answer and FAQ rather than by a second page.
      { title: `Palworld Pals: All ${PAL_COUNT} Pals, Stats & Cards` },
      { name: "description", content: "Every Palworld Pal in one searchable database: element, rarity, HP, attack, work suitability, mount speed, drops and printed TCG card number." },
      { property: "og:title", content: "Palworld Pals — Full Pal Database" },
      { property: "og:description", content: "All Palworld Pals with stats, elements, work suitability and card data." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/pals" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld Pals — Full Pal Database" },
      { name: "twitter:description", content: "All Palworld Pals with stats, elements, work suitability and card data." },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/pals" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Pal Database", "/pals"]]),
      faqSchema(FAQ, "https://palstars.com/pals"),
      // Structural markup for the Pal roster itself, so the data is machine
      // readable from the markup rather than only from the rendered table.
      datasetSchema({
        name: "Palworld Pal database",
        description: `Structured stat sheet for all ${PAL_COUNT} Palworld Pals: element, rarity, HP, attack, defense, speed, ride speed, stamina, capture rate, work suitabilities, partner skill, drops, spawn region and printed TCG card number.`,
        path: "/pals",
        keywords: ["palworld pals", "palworld pal database", "palworld pal stats", "palworld pal list", "palworld pal elements", "palworld capture rates"],
        variables: ["element", "rarity", "HP", "attack", "defense", "speed", "rideSpeed", "stamina", "captureRate", "workSuitability", "partnerSkill", "drops", "spawnRegion", "tcgCardNumber"],
      }),
      {
        // Declares the on-page filter as a real search endpoint so crawlers can
        // reach filtered Pal views without scraping the client-side grid.
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SearchAction",
          target: { "@type": "EntryPoint", urlTemplate: "https://palstars.com/pals?q={search_term_string}" },
          "query-input": "required name=search_term_string",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "All Pals — Pal Stars",
          numberOfItems: PAL_COUNT,
          itemListElement: PAL_SCHEMA_SAMPLE.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://palstars.com/pals/${p.slug}`,
            name: p.name,
          })),
        }),
      },
    ],
  }),
});
